import React, { useState } from 'react';
import { ArrowLeft, Download, Share2, Edit, Copy, ChevronLeft, ChevronRight, Presentation, Check, DollarSign, Calendar, Gift, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';

const TaxReturnSlideDeck: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Slide content for the tax return qualification presentation
  const slides = [
    {
      id: 1,
      title: "Do you qualify for a professionally completed tax return — at NO Additional Cost — for the upcoming tax season?",
      content: (
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-primary mb-8">Do you qualify for a professionally completed tax return — at NO Additional Cost — for the upcoming tax season?</h1>
          <div className="bg-primary/10 p-8 rounded-xl">
            <p className="text-xl mb-6">If you're 50 or older and have saved $100,000 or more, join us for a Free Retirement Income & Tax Efficiency Seminar.</p>
            <p className="text-lg text-gray-700">Discover how to protect your retirement savings and optimize your future.</p>
          </div>
        </div>
      ),
      notes: "Opening slide to grab attention with the tax return offer at no additional cost. Emphasize qualification criteria: age 50+ and $100k+ in savings."
    },
    {
      id: 2,
      title: "Key Benefits",
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primary mb-10">Key Benefits You'll Discover</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="bg-primary/10 p-2 rounded-full mr-4">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Eliminate income gaps in retirement</h3>
                  <p className="text-gray-600">Learn strategies to ensure consistent income throughout your retirement years.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="bg-primary/10 p-2 rounded-full mr-4">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Reduce taxes on IRAs, 401(k)s, and Social Security</h3>
                  <p className="text-gray-600">Discover legal tax reduction strategies specifically for retirement accounts.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="bg-primary/10 p-2 rounded-full mr-4">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Protect assets from market downturns</h3>
                  <p className="text-gray-600">Learn how to safeguard your retirement savings from market volatility.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-start">
                <div className="bg-primary/10 p-2 rounded-full mr-4">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Professional tax return preparation</h3>
                  <p className="text-gray-600">Qualified attendees receive professional tax preparation at NO additional cost for the upcoming tax season.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      notes: "Highlight the four key benefits attendees will learn about. Emphasize the value of each benefit and how it addresses common retirement concerns."
    },
    {
      id: 3,
      title: "Professional Tax Return Preparation at NO Additional Cost",
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primary mb-8">Professional Tax Return Preparation at NO Additional Cost</h2>
          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200 mb-8">
            <div className="flex items-center justify-center mb-6">
              <DollarSign className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-center mb-4">$350 Value at No Cost to You</h3>
            <p className="text-lg text-center text-gray-700 mb-6">
              Let our Enrolled Agents professionally prepare your tax return — at no additional cost — for the upcoming tax season.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                <p className="text-sm text-yellow-700">
                  This offer is exclusively available to seminar attendees who meet the qualification criteria.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <DollarSign className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold mb-1">Professional Expertise</h4>
              <p className="text-sm text-gray-600">Prepared by qualified tax professionals</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <Check className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold mb-1">Accuracy Guaranteed</h4>
              <p className="text-sm text-gray-600">100% accuracy guarantee on all returns</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <Calendar className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold mb-1">Timely Filing</h4>
              <p className="text-sm text-gray-600">Ensure on-time filing for maximum benefits</p>
            </div>
          </div>
        </div>
      ),
      notes: "Emphasize the value of the tax return preparation at no additional cost ($350 value). Highlight the expertise of our Enrolled Agents and the benefits of professional preparation."
    },
    {
      id: 4,
      title: "Bonus Offer",
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primary mb-8">Special Bonus Offer</h2>
          <div className="bg-white p-8 rounded-xl shadow-md border border-primary/20 mb-8">
            <div className="flex items-center justify-center mb-6">
              <Gift className="h-16 w-16 text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-center mb-4">Complimentary 1-on-1 Retirement Strategy Session</h3>
            <p className="text-lg text-center text-gray-700 mb-6">
              All attendees qualify for a personalized retirement strategy session with one of our financial advisors.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Personalized Analysis
                </h4>
                <p className="text-sm text-gray-600">
                  Custom analysis of your current retirement strategy and potential improvements
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Tax Efficiency Review
                </h4>
                <p className="text-sm text-gray-600">
                  Identify opportunities to reduce your tax burden in retirement
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Income Planning
                </h4>
                <p className="text-sm text-gray-600">
                  Strategies to maximize your retirement income from all sources
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Risk Assessment
                </h4>
                <p className="text-sm text-gray-600">
                  Evaluation of your portfolio's risk exposure and protection strategies
                </p>
              </div>
            </div>
          </div>
          <p className="text-center text-lg font-medium text-primary">
            A $500 value, yours at no cost as a seminar attendee!
          </p>
        </div>
      ),
      notes: "Highlight the additional bonus of a complimentary 1-on-1 retirement strategy session. Emphasize the $500 value and the personalized nature of this consultation."
    },
    {
      id: 5,
      title: "Limited Seating",
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primary mb-8">Limited to 20 Seats – Reserve Yours Now!</h2>
          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Upcoming Seminar Details:</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Calendar className="h-6 w-6 text-primary mr-3 mt-1" />
                    <div>
                      <p className="font-semibold">Date & Time</p>
                      <p className="text-gray-600">[Insert Date] at [Insert Time]</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Presentation className="h-6 w-6 text-primary mr-3 mt-1" />
                    <div>
                      <p className="font-semibold">Location</p>
                      <p className="text-gray-600">[Insert Location Name]</p>
                      <p className="text-gray-600">[Insert Address]</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-center">How to Reserve Your Seat</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="bg-primary text-white rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">1</div>
                    <p className="text-gray-700">Call our reservation line at [Phone Number]</p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary text-white rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">2</div>
                    <p className="text-gray-700">Register online at [Website/URL]</p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary text-white rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-1">3</div>
                    <p className="text-gray-700">Scan the QR code on your invitation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
              <p className="text-yellow-700">
                <span className="font-semibold">Important:</span> Due to the exclusive nature of this offer and limited seating, reservations are required and will be confirmed on a first-come, first-served basis.
              </p>
            </div>
          </div>
        </div>
      ),
      notes: "Create urgency with the limited seating (20 seats only). Provide clear instructions on how to register. Include placeholder text for date, time, and location that can be customized for each event."
    },
    {
      id: 6,
      title: "Qualification Reminder",
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primary mb-8">Remember: You May Qualify for Professional Tax Return Preparation at NO Additional Cost</h2>
          <div className="bg-primary/10 p-8 rounded-xl mb-8">
            <div className="flex flex-col items-center mb-6">
              <DollarSign className="h-16 w-16 text-primary mb-4" />
              <h3 className="text-2xl font-bold text-center">Qualification Criteria:</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="bg-primary text-white rounded-full h-8 w-8 flex items-center justify-center mr-3">1</div>
                  <h4 className="text-xl font-semibold">Age 50 or older</h4>
                </div>
                <p className="text-gray-600 ml-11">
                  This offer is specifically designed for individuals planning for or approaching retirement.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="bg-primary text-white rounded-full h-8 w-8 flex items-center justify-center mr-3">2</div>
                  <h4 className="text-xl font-semibold">$100,000+ in savings</h4>
                </div>
                <p className="text-gray-600 ml-11">
                  Have at least $100,000 in retirement accounts, investments, or savings.
                </p>
              </div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xl font-medium text-gray-800 mb-4">
              Attend our seminar to learn valuable retirement strategies AND qualify for professional tax preparation at no additional cost.
            </p>
            <p className="text-lg text-gray-600">
              A $350 value – completely free for qualified attendees!
            </p>
          </div>
        </div>
      ),
      notes: "Reinforce the qualification criteria (age 50+ and $100k+ in savings) and the value of the tax return preparation at no additional cost. This slide serves as a reminder of the main offer."
    },
    {
      id: 7,
      title: "Call to Action",
      content: (
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-primary mb-8">Reserve Your Seat Today!</h2>
          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200 mb-8">
            <div className="flex flex-col items-center mb-6">
              <Calendar className="h-16 w-16 text-primary mb-4" />
              <h3 className="text-2xl font-bold">Limited to 20 Attendees</h3>
              <p className="text-lg text-gray-600 mt-2">
                Don't miss this opportunity to secure your financial future and receive professional tax preparation at no additional cost.
              </p>
            </div>
            <div className="bg-primary text-white p-6 rounded-lg max-w-md mx-auto">
              <p className="text-xl font-bold mb-2">Call Now to Reserve:</p>
              <p className="text-2xl font-bold">[Phone Number]</p>
              <p className="mt-2 text-white/80">or</p>
              <p className="text-xl font-bold mt-2">Visit: [Website/URL]</p>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <Gift className="h-5 w-5" />
            <p>Remember: Tax return preparation at no additional cost ($350 value) and complimentary retirement strategy session ($500 value)</p>
          </div>
        </div>
      ),
      notes: "Strong call to action slide. Emphasize the limited seating and the combined value of the offers (tax return at no additional cost + strategy session = $850 value). Include placeholder text for contact information that can be customized."
    }
  ];

  const goToNextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const goToPrevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNextSlide();
      } else if (e.key === 'ArrowLeft') {
        goToPrevSlide();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, slides.length]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/dashboard/landing-pages" className="mr-4">
              <ArrowLeft className="h-5 w-5 text-gray-600 hover:text-gray-900" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Tax Return Qualification Slide Deck</h1>
              <p className="text-sm text-gray-500">Presentation for tax return qualification seminar</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" className="flex items-center">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" className="flex items-center">
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button variant="outline" className="flex items-center">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button className="flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow flex flex-col md:flex-row">
        {/* Slide Navigator */}
        <div className="w-full md:w-64 bg-gray-800 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-white text-sm font-medium mb-4">Slides</h2>
            <div className="space-y-2">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`cursor-pointer rounded-lg overflow-hidden ${
                    currentSlide === index ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setCurrentSlide(index)}
                >
                  <div className="relative bg-white p-2">
                    <div className="h-20 flex items-center justify-center overflow-hidden bg-gray-100 rounded">
                      <p className="text-sm font-medium text-gray-800 px-2 truncate">
                        {slide.title}
                      </p>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-gray-800 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                      {index + 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slide Viewer */}
        <div className="flex-grow flex flex-col">
          <div className="flex-grow flex items-center justify-center bg-gray-900 relative p-8">
            <div className="bg-white rounded-lg w-full max-w-5xl aspect-[16/9] overflow-hidden flex items-center justify-center">
              <div className="w-full h-full p-12 overflow-auto">
                {slides[currentSlide]?.content}
              </div>
            </div>
            
            {/* Navigation Controls */}
            <button
              onClick={goToPrevSlide}
              disabled={currentSlide === 0}
              className={`absolute left-4 p-2 rounded-full bg-white/80 hover:bg-white ${
                currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ChevronLeft className="h-6 w-6 text-gray-800" />
            </button>
            
            <button
              onClick={goToNextSlide}
              disabled={currentSlide === slides.length - 1}
              className={`absolute right-4 p-2 rounded-full bg-white/80 hover:bg-white ${
                currentSlide === slides.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ChevronRight className="h-6 w-6 text-gray-800" />
            </button>
            
            {/* Slide Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>

          {/* Speaker Notes */}
          {slides[currentSlide]?.notes && (
            <div className="bg-white border-t border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Presentation className="h-4 w-4 mr-2" />
                Speaker Notes
              </h3>
              <p className="text-gray-600 text-sm">{slides[currentSlide].notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxReturnSlideDeck;